// ═══════════════════════════════════════════════════════════════
// vaultApi.js — Client /api/vault/* (Coffre-fort utilisateur, Session F)
// ═══════════════════════════════════════════════════════════════
// 4 endpoints essentiels MVP : create / list / read / delete.
// Polish (restore, export-zip, settings, access-log) → Session F+1.
// Pattern aligné sur familyApi.js.

const getToken = () => localStorage.getItem('oracle_token');

export class VaultApiError extends Error {
    constructor(status, code, message, detail) {
        super(message || code || `HTTP ${status}`);
        this.name = 'VaultApiError';
        this.status = status;
        this.code = code;
        this.detail = detail;
    }
}

async function request(path, { method = 'GET', body = null, timeoutMs = 30000 } = {}) {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), timeoutMs);
    try {
        const res = await fetch(path, {
            method,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`,
                // Items vault sont sensibles — ne JAMAIS cacher côté navigateur.
                'Cache-Control': 'no-store',
            },
            body: body ? JSON.stringify(body) : undefined,
            signal: ctl.signal,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            throw new VaultApiError(
                res.status,
                data.error,
                data.message || data.error || `HTTP ${res.status}`,
                data
            );
        }
        return data;
    } finally {
        clearTimeout(timer);
    }
}

/**
 * POST /api/vault/items — chiffre un bloc de message et le déplace au coffre-fort.
 * Purge cascade : la conversation source est soft-deleted, Weaviate purgé,
 * insights trans-projet retirés, résumé invalidé.
 *
 * @param {{ projectId: number, content: string, sourceConversationId?: number, role?: 'user'|'assistant' }} payload
 */
export const sendToVault = (payload) =>
    request('/api/vault/items', { method: 'POST', body: payload });

/**
 * GET /api/vault/items?projectId=X — liste des items du user.
 * @param {number?} projectId — filtre optionnel par projet (omettre = tous projets)
 */
export const listVaultItems = (projectId = null) => {
    const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
    return request(`/api/vault/items${qs}`);
};

/**
 * GET /api/vault/items/:id — déchiffre et retourne plaintext (owner only).
 */
export const readVaultItem = (id) =>
    request(`/api/vault/items/${id}`);

/**
 * DELETE /api/vault/items/:id — soft-delete.
 */
export const deleteVaultItem = (id) =>
    request(`/api/vault/items/${id}`, { method: 'DELETE' });

/**
 * POST /api/vault/items/:id/restore — restauration soft (30j).
 */
export const restoreVaultItem = (id) =>
    request(`/api/vault/items/${id}/restore`, { method: 'POST' });

/**
 * GET /api/vault/items/trash — items soft-deleted des 30 derniers jours
 * (récupération possible). Renvoie remainingDays par item.
 */
export const listVaultTrash = () => request('/api/vault/items/trash');

/**
 * POST /api/vault/export-zip — export chiffré (passphrase user, jamais stockée).
 * Retourne l'enveloppe chiffrée (à télécharger). Format : itriia-vault-export-v1.
 */
export const exportVault = (passphrase) =>
    request('/api/vault/export-zip', { method: 'POST', body: { passphrase }, timeoutMs: 90000 });

/**
 * GET /api/vault/settings — settings + métadonnées coffre.
 */
export const getVaultSettings = () => request('/api/vault/settings');

/**
 * PUT /api/vault/settings — body { burnThreshold?, autoBurn? }.
 */
export const updateVaultSettings = (patch) =>
    request('/api/vault/settings', { method: 'PUT', body: patch });

/**
 * GET /api/vault/access-log?limit=50 — historique d'accès (transparence).
 */
export const getVaultAccessLog = (limit = 50) =>
    request(`/api/vault/access-log?limit=${encodeURIComponent(limit)}`);

/**
 * POST /api/vault/unlock — reset failed_attempts si lock_until expiré.
 */
export const unlockVault = () =>
    request('/api/vault/unlock', { method: 'POST' });
